/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { Box, Center, Flex, IconButton, Text, Tooltip, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { DownloadIcon } from '@chakra-ui/icons';
import { chartBackgroundColor } from '../../Miscellaneous/plugins-chart';
import Chart from 'react-chartjs-2';
import chartDataLabels from 'chartjs-plugin-datalabels';
import DataLoading from '../../../DataLoading';

const Q_DATASET = gql`
	query get($companyType: InsuranceInstitutionsType, $companyId: String, $range: MDDataDateRangeInput) {
		getAnnuallyClaimByProductLineDashboardDataset(companyType: $companyType, companyId: $companyId, range: $range)
	}
`;

const chartDataOutsideLabels = {
	id: 'chartDataOutsideLabels',
	afterDraw(chart) {
		const {
			ctx,
			chartArea: { width, height },
		} = chart;
		const cx = chart._metasets[0].data[0].x;
		const cy = chart._metasets[0].data[0].y;
		chart.data.datasets.forEach((dataset, i) => {
			chart.getDatasetMeta(i).data.forEach((datapoint, index) => {
				const { x: a, y: b } = datapoint.tooltipPosition();
				const x = 2 * a - cx;
				const y = 2 * b - cy;
				const halfWidth = width / 2;
				const halfHeight = height / 2;
				const xLine = x > halfWidth ? x + 32 : x - 32;
				const yLine = y > halfHeight ? y : y;
				const space = x > halfWidth ? 2 : -2;
				ctx.setLineDash([1, 1]);
				ctx.beginPath();
				ctx.moveTo(x, y);
				ctx.lineTo(xLine, yLine);
				ctx.strokeStyle = dataset?.backgroundColor[index];
				ctx.stroke();
				ctx.font = '10px Arial';
				const textXPosition = x >= halfWidth ? 'left' : 'right';
				ctx.textAlign = textXPosition;
				ctx.textBaseline = 'middle';
				ctx.setLineDash([0, 0]);
				ctx.fillStyle = dataset.backgroundColor[index];
				ctx.fillText(chart.data.labels[index], xLine + space, yLine - 6);
				ctx.fillText(dataset.data[index] + '%', xLine + space, yLine + 6);
			});
		});
	},
};

function Graph({ companyType = null, companyId = null, range = null }) {
	const chartRef = useRef(null);
	const toast = useToast();
	const { t } = useTranslation();
	const [getDataset, { data, error, loading }] = useLazyQuery(Q_DATASET, { fetchPolicy: 'no-cache' });

	useEffect(load, [companyType, companyId, range]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { companyType, companyId, range: range?.from && range?.to ? range : null };
		getDataset({ variables });
	}

	return (
		<Box className={loading ? 'disableFormClear' : ''}>
			<Box border="1px dotted #bdc3c7">
				<Flex justifyContent="center" alignItems="center" flexDirection="column" p={4}>
					<Text fontSize="lg" fontWeight="bold" color="gray.600" noOfLines={1} pl={4} pr={4}>
						{t(companyType === 'GENERAL_INSURANCE' || companyType === 'MICRO_INSURANCE' ? 'Claim by Product Line' : 'Claim by Product Type')}
					</Text>
				</Flex>
				<DataLoading loading={loading} loadedData={data?.getAnnuallyClaimByProductLineDashboardDataset}>
					{({ loadedData }) => (
						<>
							<Box width="100%" p={4} pt={0}>
								<Center>
									<Chart
										ref={chartRef}
										type="pie"
										width="276px"
										height="276px"
										plugins={[chartDataLabels, chartDataOutsideLabels, chartBackgroundColor]}
										options={{
											maintainAspectRatio: false,
											canvasBackgroundColor: {
												color: '#FFFFFF',
											},
											layout: {
												padding: {
													top: 32,
													right: 16,
													bottom: 16,
													left: 8,
												},
											},
											plugins: {
												title: { display: false },
												legend: {
													display: false,
													position: 'bottom',
												},
												datalabels: {
													display: true,
													color: 'white',
													font: {
														family: 'Rajdhani, Hanuman, sans-serif, Moul',
														weight: 'bold',
														size: 10,
													},
													formatter: function (value, context) {
														return value + '%';
													},
												},
												tooltip: {
													callbacks: {
														label: (toolTipItem) => {
															return t(toolTipItem.label) + ': ' + toolTipItem.formattedValue + '%';
														},
													},
												},
											},
										}}
										data={loadedData}
									/>
								</Center>
							</Box>
							<Flex className="hidden-print" justifyContent="flex-end" alignItems="center" p={4}>
								<Tooltip label={t('Download Image')}>
									<IconButton
										size="sm"
										icon={<DownloadIcon w={3} h={3} />}
										onMouseDown={() => {
											const link = document.createElement('a');
											link.download = `graph-claim-gross-premium.jpg`;
											link.href = chartRef?.current?.toBase64Image('image/jpeg', 1);
											link.click();
										}}
									/>
								</Tooltip>
							</Flex>
						</>
					)}
				</DataLoading>
			</Box>
		</Box>
	);
}

export default Graph;
